import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath, useLocation} from 'react-router-dom';

import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {ReactComponent as FilterIcon} from 'assets/svg/filter2.svg';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {DateRange} from 'components/ui-kit/DateRange';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {CopyButton, StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {ProductGroups} from 'core/constants/productGroups';
import {AUTHENTIC_STATUS_COLORS, PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {AuthenticStatus, ProductsPackageType, ProductsStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {listState$, showConfigBlockChanged} from 'models/products/grid/list';
import {productsNames$, storageLocations$} from 'models/products/grid/table';
import {breakpoint, colors} from 'theme';
import {GridFieldConfig, UserPermission} from 'types';
import {CUSTOMER_PACKAGE_TYPES, PackageTypes, TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {
    ProductsGridAccessors,
    ProductsGridFormattedRow,
    ProductsGridRawRow,
} from 'types/products-grid';
import {useClipboard, useMatchMedia} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {PackageTypesOptions} from 'utils/packageTypes-mappers';
import {mapArrayToI18nOptions, mapEnumToI18nOptions} from 'utils/select';
import {toHex} from 'utils/string';

import {ProductsGridList} from './List';
import {ProductsGridTable} from './Table';

export const ProductsGridPage = () => {
    const {t} = useTranslation();
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const {showConfigBlock} = useStore(listState$);
    const location = useLocation();

    const copy = useClipboard();
    const copyCode = (string: string) => {
        copy(string);
        toast.success(t('pages.documents.details.idCopied'));
    };

    const getGridColumns = (t: TFunction): GridFieldConfig[] => {
        const productGroup = {
            id: ProductsGridAccessors.productGroupCode,
            header: t('pages.products.grid.productGroup'),
            filter: (
                <Select
                    options={mapEnumToI18nOptions(ProductGroups, 'dictionaries.productGroups')}
                    isClearable
                    isMulti
                />
            ),
            isSortable: false,
            ignoreConfig: true,
            width: 150,
        };

        const productName = {
            id: ProductsGridAccessors.name,
            header: t('pages.products.grid.productName'),
            filter: (
                <CheckboxArray
                    searchable={true}
                    placeholder="Enter the product name"
                    name="product-name-filter"
                    options={() => productsNames$.getState()}
                />
            ),
            width: 150,
        };

        const code = {
            id: ProductsGridAccessors.code,
            header: t('pages.products.grid.code'),
            isSortable: false,
            width: 250,
        };

        const parentCode = {
            id: ProductsGridAccessors.parentMarkingCode,
            header: t('pages.products.grid.parentCode'),
            isSortable: false,
            filter: <Input placeholder={t('Enter value')} />,
            width: 220,
        };

        const authenticStatus = {
            id: ProductsGridAccessors.authenticStatus,
            header: t('pages.products.grid.authenticStatus'),
            filter: (
                <CheckboxArray
                    name="authentic-status-filter"
                    options={() =>
                        mapEnumToI18nOptions(AuthenticStatus, 'dictionaries.authenticStatus')
                    }
                />
            ),
            isSortable: false,
            width: 130,
        };

        const modifiedAt = {
            id: ProductsGridAccessors.modifiedAt,
            header: t('pages.products.grid.changeDate'),
            filter: <DateRange />,
            isSortable: true,
            width: 150,
        };

        const PackageTypeOptionsByCodeType: Record<string, any> = {
            [ROUTE.PRODUCTS_CONSUMER_GRID]: PackageTypesOptions.filter((p) =>
                CUSTOMER_PACKAGE_TYPES.includes(p.value),
            ),
            [ROUTE.PRODUCTS_TRANSPORT_GRID]: PackageTypesOptions.filter((p) =>
                TRANSPORT_PACKAGE_TYPES.includes(p.value),
            ),
        };
        const packageType = {
            id: ProductsGridAccessors.packageType,
            header: t('pages.products.grid.package'),
            filter: (
                <CheckboxArray
                    name="package-type-filter"
                    options={() =>
                        mapArrayToI18nOptions(
                            PackageTypeOptionsByCodeType[location.pathname] ?? PackageTypesOptions,
                            'dictionaries.products.packageType',
                        )
                    }
                />
            ),
            isSortable: false,
            width: 120,
        };

        const storageName = {
            id: ProductsGridAccessors.storageLocationName,
            header: t('pages.products.grid.storageLocationName'),
            isSortable: false,
            filter: (
                <CheckboxArray
                    searchable={true}
                    placeholder="Enter the name or address"
                    name="storage-location-filter"
                    options={() => storageLocations$.getState()}
                />
            ),
            width: 200,
        };

        const orderId = {
            id: ProductsGridAccessors.orderId,
            header: t('pages.products.grid.orderId'),
            isSortable: false,
            width: 220,
        };

        const owner = {
            id: ProductsGridAccessors.ownerTinOrName,
            header: t('pages.products.grid.owner'),
            filter: <Input placeholder={t('pages.products.grid.enterValue')} />,
            isSortable: false,
            width: 250,
        };

        const status = {
            id: ProductsGridAccessors.status,
            header: t('pages.products.grid.status'),
            filter: (
                <CheckboxArray
                    name="status-filter"
                    options={() =>
                        mapEnumToI18nOptions(ProductsStatus, 'dictionaries.products.status')
                    }
                />
            ),
            isSortable: false,
            width: 160,
        };

        const columnsMapping: Record<string, GridFieldConfig[]> = {
            [ROUTE.PRODUCTS_CONSUMER_GRID]: [
                productGroup,
                productName,
                packageType,
                code,
                modifiedAt,
                parentCode,
                status,
                authenticStatus,
                storageName,
                orderId,
                owner,
            ],
            [ROUTE.PRODUCTS_TRANSPORT_GRID]: [
                packageType,
                code,
                modifiedAt,
                parentCode,
                status,
                storageName,
                orderId,
                owner,
            ],
            [ROUTE.PRODUCTS_CUSTOMS_GRID]: [
                code,
                modifiedAt,
                parentCode,
                status,
                storageName,
                orderId,
                owner,
            ],
        };

        return columnsMapping[location.pathname] ?? columnsMapping[ROUTE.PRODUCTS_CONSUMER_GRID];
    };

    const convertData = (
        data: ProductsGridRawRow[] | undefined,
        t: TFunction,
    ): ProductsGridFormattedRow[] => {
        if (!data) return [];

        return data.map((elem) => ({
            [ProductsGridAccessors.productGroupCode]: handleNil(elem, (row: ProductsGridRawRow) =>
                [
                    PackageTypes.CUSTOMS_UNIT,
                    PackageTypes.BOX,
                    PackageTypes.PALLET,
                    PackageTypes.CONTAINER,
                ].includes(row.packagingType) ? (
                    <div>—</div>
                ) : (
                    <ProductGroupIcon value={row.productGroupCode} label />
                ),
            ),
            [ProductsGridAccessors.name]: handleNil(elem.productName),
            [ProductsGridAccessors.parentMarkingCode]: handleNil(
                elem.parentMarkingCode,
                (value: string) => (
                    <PermissionAccess
                        permissions={[
                            UserPermission.PRODUCT_DETAIL_VIEW,
                            UserPermission.PRODUCT_CARD_VIEW,
                        ]}
                        placeholder={value}
                    >
                        {value && elem.parentGtin ? (
                            <Link
                                to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                                    id: toHex(`${elem.parentGtin}§${value}`),
                                })}
                                style={{wordBreak: 'break-all'}}
                            >
                                {value}
                            </Link>
                        ) : (
                            <div>—</div>
                        )}
                    </PermissionAccess>
                ),
            ),
            [ProductsGridAccessors.storageLocationName]: handleNil(elem.storageLocationName),
            [ProductsGridAccessors.orderId]: handleNil(elem.orderId, (value) => (
                <div style={{display: 'flex', gap: '8px'}}>
                    {value}
                    <CopyButton
                        icon={
                            <Icon
                                source={CopyIcon}
                                width={15}
                                height={15}
                                fillColor={colors.brandPrimary}
                            />
                        }
                        onClick={() => copyCode(value)}
                        hoverIndication={true}
                        label={t('pages.documents.details.copyId')}
                        labelPlace={'bottom'}
                    />
                </div>
            )),
            [ProductsGridAccessors.authenticStatus]: (
                <StatusWrapper color={AUTHENTIC_STATUS_COLORS[elem.authenticStatus ?? 'DEFAULT']}>
                    {elem.authenticStatus
                        ? t(`dictionaries.authenticStatus.${elem.authenticStatus}`)
                        : '—'}
                </StatusWrapper>
            ),
            [ProductsGridAccessors.code]: handleNil(elem.markingCode, (value: string) => (
                <PermissionAccess
                    permissions={[
                        UserPermission.PRODUCT_DETAIL_VIEW,
                        UserPermission.PRODUCT_CARD_VIEW,
                    ]}
                    placeholder={value}
                >
                    <Link
                        to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                            id: toHex(`${elem.gtin}§${value}`),
                        })}
                        style={{wordBreak: 'break-all'}}
                    >
                        {value}
                    </Link>
                </PermissionAccess>
            )),
            [ProductsGridAccessors.status]: handleNil(elem.status, (value: ProductsStatus) => (
                <StatusWrapper color={PRODUCT_STATUS_COLORS[value ?? 'DEFAULT']}>
                    {t(`dictionaries.products.status.${value}`)}
                </StatusWrapper>
            )),
            [ProductsGridAccessors.modifiedAt]: handleNil(elem.modifiedAt, (value: string) =>
                formatDate(value, 'dd.MM.yyyy'),
            ),
            [ProductsGridAccessors.packageType]: handleNil(
                elem.packagingType,
                (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
            ),
            [ProductsGridAccessors.ownerTinOrName]: handleNil(
                elem.owner,
                (value: ProductsGridRawRow['owner']) => (
                    <div style={{display: 'flex', flexDirection: 'column', fontSize: 12}}>
                        {value ?? '—'}
                        <span style={{color: colors.textSecondary}}>
                            {elem[ProductsGridAccessors['ownerTin']] ?? '—'}
                        </span>
                    </div>
                ),
            ),
            [ProductsGridAccessors.manufacturer]: handleNil(elem.manufacturerName),
        }));
    };

    const pageHeaderControls = isMobile ? (
        <Icon
            source={FilterIcon}
            width={18}
            height={18}
            onClick={() => showConfigBlockChanged(!showConfigBlock)}
            fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
        />
    ) : (
        undefined
    );

    return (
        <Page title={t('pages.products.grid.markingCodes')} controls={pageHeaderControls}>
            {!isMobile && (
                <div style={{height: 'calc(100vh - 150px)'}}>
                    <ProductsGridTable
                        configFunc={getGridColumns}
                        dataMapper={convertData}
                        // exportConfig={exportConfig}
                    />
                </div>
            )}
            {isMobile && (
                <ProductsGridList
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                    // exportConfig={exportConfig}
                />
            )}
        </Page>
    );
};
